import { Selection, EnterElement } from "d3"
import { D3VisualizationRenderer } from "../../D3VisualizationRenderer";
import { D3ScatterPlot } from "./D3ScatterPlot";
import { D3ScatterPlotConfigurationBuilder } from "./D3ScatterPlotConfigurationBuilder";
import { D3ScatterPlotXAxis } from "./D3ScatterPlotXAxis";
import { D3ScatterPlotYAxis } from "./D3ScatterPlotYAxis";
import { D3Timeline } from "../../../D3/Timeline/D3Timeline";
import { D3ScatterPlotCanvas } from "./D3ScatterPlotCanvas";
import { getDataInView } from "../../../../Data/TimeSeriesData";
import { DataSource } from "../../../../Types/DataSource";
import { TraceDataConfig } from "../../../../Types/Trace";
import { throttledRender } from "../../../../rendering";
import { finiteExtent } from "../../../../Utils/finiteExtent";

export class D3ScatterPlotRenderer extends D3VisualizationRenderer<D3ScatterPlot, D3ScatterPlotConfigurationBuilder> {
	public className: string = "d3-scatter-plot"

	public xAxis?: D3ScatterPlotXAxis
	public yAxis?: D3ScatterPlotYAxis
	private timelineSpacing = 45
	public scatterPlotCanvas?: D3ScatterPlotCanvas
	private timelineContainerClassName: string = "d3-scatter-plot-timeline"

	public viewTimesChanged(): void {
		this.timeline?.viewTimesChanged()
		this.updateData()
	}

	public onTimelineSliderDrag(): void {
		this.timeline?.viewTimesChanged()
		this.updateData()
	}

	protected enter(enterElements: Selection<EnterElement, any, any, any>): Selection<any, any, any, any> {
		const svg = enterElements.append("svg").attr("class", this.className).attr("width", "100%").attr("height", "100%")

		// Add legend
		const legendGroup = svg.append("g")
			.attr("class", "d3-scatter-plot-legend")
			.style('font-family', '"source-sans-pro", sans-serif')
			.style("font-weight", "bold")
			.style("font-size", "14px")

		// Y-Axis Legend
		legendGroup.append("text")
			.attr("class", "d3-scatter-plot-legend-yAxis")
			.text(`[${this.visualization.config.yAxisConfig.modality}]`)
			.attr("transform", `rotate(-90) translate(${-this.visualization.boundingBox.y}, ${this.visualization.boundingBox.x - 40})`)
			.style("text-anchor", "end")
			.attr("fill", "var(--color-chart-axis-text)")

		// X-Axis Legend
		legendGroup.append("text")
			.attr("class", "d3-scatter-plot-legend-xAxis")
			.text(`[${this.visualization.config.xAxisConfig.modality}]`)
			.style("text-anchor", "end")
			.attr("x", this.visualization.boundingBox.width + this.visualization.boundingBox.x)
			.attr("y", this.visualization.boundingBox.height + this.visualization.boundingBox.y + 35)
			.attr("fill", "var(--color-chart-axis-text)")

		const boundingBox = svg
			.append("g")
			.attr("class", "d3-scatter-plot-bounding-box")
			.attr("transform", `translate(${this.visualization.boundingBox.x}, ${this.visualization.boundingBox.y})`)

		boundingBox.each((config, index, nodes) => this.createBoundingBoxChildren(config, index, nodes))


		const timelineContainer = svg
			.append('g')
			.attr("class", this.timelineContainerClassName)
			.attr("transform", `translate(${this.visualization.boundingBox.x}, ${this.visualization.boundingBox.y + this.visualization.boundingBox.height + this.timelineSpacing})`)

		timelineContainer.each((config, index, nodes) => {
			this.timeline = new D3Timeline(nodes[index], this.configBuilder.getTimelineConfig(), this.visualization.timeSeriesPageManager, this.visualization.reactCallbacks)
		})

		return svg
	}

	protected update = (updatedSVG: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		const svg = updatedSVG

		// Update the positions and text of the legends
		const legendGroup = svg.select(".d3-scatter-plot-legend")

		// Update Y-Axis Legend
		legendGroup.select(".d3-scatter-plot-legend-yAxis")
			.text(`[${this.visualization.config.yAxisConfig.modality}]`)
			.attr("transform", `rotate(-90) translate(${-this.visualization.boundingBox.y}, ${this.visualization.boundingBox.x - 40})`)

		// Update X-Axis Legend
		legendGroup.select(".d3-scatter-plot-legend-xAxis")
			.text(`[${this.visualization.config.xAxisConfig.modality}]`)
			.attr("x", this.visualization.boundingBox.width + this.visualization.boundingBox.x)
			.attr("y", this.visualization.boundingBox.height + this.visualization.boundingBox.y + 35)

		const timelineContainer = svg.select("." + this.timelineContainerClassName)
		timelineContainer.attr("transform", `translate(${this.visualization.boundingBox.x}, ${this.visualization.boundingBox.y + this.visualization.boundingBox.height + this.timelineSpacing})`)

		this.timeline?.render()

		return svg
	}

	protected canRender(): boolean {
		return this.visualization.boundingBox.height > 0 && this.visualization.boundingBox.width > 0
	}

	private createBoundingBoxChildren = (config: D3ScatterPlot, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]

		this.xAxis = new D3ScatterPlotXAxis(root, this.configBuilder.getXAxisConfig(), this.visualization.reactCallbacks)
		this.yAxis = new D3ScatterPlotYAxis(root, this.configBuilder.getYAxisConfig(), this.visualization.reactCallbacks)
		this.scatterPlotCanvas = new D3ScatterPlotCanvas(root, this.configBuilder.getScatterPlotCanvasConfig(), this.visualization.reactCallbacks)
	}

	public updateData = throttledRender(() => {
		const dataObjectId = this.visualization.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number

		const xConfig: TraceDataConfig = {
			id: "x",
			dataKey: this.visualization.config.xAxisConfig.modality,
			dataSource: DataSource.CURRENT_PATIENT,
			isCompositePart: false,
			name: this.visualization.config.xAxisConfig.modality,
			units: "?",
			rawDataKey: this.visualization.config.xAxisConfig.modality
		}

		const yConfig: TraceDataConfig = {
			id: "y",
			dataKey: this.visualization.config.yAxisConfig.modality,
			dataSource: DataSource.CURRENT_PATIENT,
			isCompositePart: false,
			name: this.visualization.config.yAxisConfig.modality,
			units: "?",
			rawDataKey: this.visualization.config.yAxisConfig.modality
		}

		const allowMissingValues = true // to ensure x and y points are aligned properly

		// Store this data separately to make it easier to autoscale.
		const xData = getDataInView(dataObjectId, this.visualization.timeSeriesPageManager as any, xConfig, this.visualization.config.viewScale, allowMissingValues)
		const yData = getDataInView(dataObjectId, this.visualization.timeSeriesPageManager as any, yConfig, this.visualization.config.viewScale, allowMissingValues)
		
		// Needed for canvasSeriesPoint rendering. Best to do this once, and not multiple times.
		this.visualization.data = xData.map((xValue, index) => ({
			color: this.visualization.config.color,
			shape: this.visualization.config.shape,
			size: this.visualization.config.size,
			x: xValue,
			y: yData[index],
			xScale: this.visualization.xScale,
			yScale: this.visualization.yScale
		}))

		this.scatterPlotCanvas?.updateData(this.visualization.data)

		if (this.visualization.config.autoScale) {
			this.autoScale()
		}
	}, 100)

	public updateChildren = () => {
		this.xAxis?.updateConfig(this.configBuilder.getXAxisConfig())
		this.yAxis?.updateConfig(this.configBuilder.getYAxisConfig())
		this.timeline?.updateConfig(this.configBuilder.getTimelineConfig())
		this.scatterPlotCanvas?.updateConfig(this.configBuilder.getScatterPlotCanvasConfig())
		this.updateData()
	}

	public renderPage = () => {
		this.updateData()
		this.renderChildren()
	}

	public onYAxisDrag = () => {
		this.scatterPlotCanvas?.render()
	}

	public onXAxisDrag = () => {
		this.scatterPlotCanvas?.render()
	}

	public onYAxisAutoScale = (updateReact=true) => {
		if (!this.visualization.data || this.visualization.data.length <= 1) {
			return
		}

		// Find both the highest and lowest Y values in the data
		const extent = finiteExtent(this.visualization.data, point => point.y)

		if (!extent) {
			return
		}

		const { min: minY, max: maxY } = extent

		// Update the Y scale domain to match the min and max Y values exactly
		// This will ensure that points are spread out to use the full screen space
		// We still apply a slight adjustment to minY and maxY to ensure that points
		// do not sit exactly on the axis boundaries, improving visibility
		const padding = maxY > minY ? (maxY - minY) * 0.05 : 1
		const adjustedMinY = minY - padding
		const adjustedMaxY = maxY + padding

		if (updateReact) {
			this.visualization.reactCallbacks.setRootConfig(previous => {
				const yAxisConfig = { ...previous.yAxisConfig }
				yAxisConfig.maxValue = adjustedMaxY
				yAxisConfig.minValue = adjustedMinY
				return { ...previous, yAxisConfig: yAxisConfig }
			})
		}

		// Ensure the minimum Y value does not go below 0 if the data is all positive
		this.visualization.yScale.domain([Math.max(0, adjustedMinY), adjustedMaxY])
		this.scatterPlotCanvas?.render()
		this.yAxis?.render()
	}

	public onXAxisAutoScale = (updateReact=true) => {
		if (!this.visualization.data || this.visualization.data.length <= 1) {
			return
		}

		// Find both the lowest and highest X values in the data
		const extent = finiteExtent(this.visualization.data, point => point.x)

		if (!extent) {
			return
		}

		const { min: minX, max: maxX } = extent

		// Add padding to minX and maxX for better visualization and to ensure
		// that points are as spread out as possible, avoiding the edges
		const padding = maxX > minX ? (maxX - minX) * 0.05 : 1
		const adjustedMinX = minX - padding
		const adjustedMaxX = maxX + padding

		if (updateReact) {
			this.visualization.reactCallbacks.setRootConfig(previous => {
				const xAxisConfig = { ...previous.xAxisConfig }
				xAxisConfig.maxValue = adjustedMaxX
				xAxisConfig.minValue = adjustedMinX
				return { ...previous, xAxisConfig: xAxisConfig }
			})
		}

		// Update the X scale domain to match the adjusted min and max X values
		this.visualization.xScale.domain([adjustedMinX, adjustedMaxX])
		this.scatterPlotCanvas?.render()
		this.xAxis?.render()
	}

	public autoScale = () => {
		const updateReact = false // No need. This gets called when autoscaling is enabled, not an update from the user.
		this.onXAxisAutoScale(updateReact)
		this.onYAxisAutoScale(updateReact)
	}

	public renderChildren() {
		this.xAxis?.render()
		this.yAxis?.render()
		this.timeline?.render()
	}
}